import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { EventLoggerService } from '@services/event-logger-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { AppConfig } from '../../app.config';
import { UserService } from '@services/user';
import {ApiClientConstant} from '@cureskin/api-client';

@Component({
  selector: 'user-profile',
  templateUrl: './user-profile.html',
  standalone: false,
})
export class UserProfileComponent implements OnChanges {
  user: any;
  appVersion: string;
  androidVersion: string;
  isInternalUser: boolean;
  ui:any = { popUpModal: { open: false } };
  experiments: Array<any> = [];
  isTicketAwaitingResponse: boolean = false;
  @Output('onPopupToggle') onPopupToggle: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input('regimens') regimens: Array<any> = [] ;
  showRefundPolicyOptions: boolean = false;
  showFeedbackOption: boolean = false;
  showSkinReportButton: boolean = false;
  showHairReportButton: boolean = false;
  showPrescriptionButton: boolean = false;

  // Configurable threshold for regimen delivery check
  private readonly deliveryThresholdDays: number = 16;
  // Time conversion constant: 1000ms * 60s * 60min * 24hrs = milliseconds per day
  private readonly MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

  constructor(private conn: ConnectionService,
              private router: Router,
              private route: ActivatedRoute,
              private readonly userService: UserService,
              private broadcast: BroadcastService,
              protected appConfig: AppConfig,
              private eventLogger: EventLoggerService,
              private appWebBridge: AppWebBridgeService) {
  }

  async ngOnInit(): Promise<void> {
    this.isInternalUser = this.conn.isInternalUser();
    this.user = this.conn.getActingUser();
    this.appVersion = `V ${this.appConfig.Shared.AppVersion}`;
    this.experiments = await this.conn.findUserActiveExperiments();
    this.experiments.forEach(async (each: any): Promise<void> => {
      if (each.key === 'new_help_ui') {
        await this.getPendingResponseMap();
      };
    });
    this.processRegimens();
    this.updatePrescriptionVisibility();
    this.updateReportButtonVisibility();
    this.androidVersion = this.appWebBridge?.requestAppVersion()
      ? (await this.appWebBridge.requestAppVersion()).toString()
      : '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['regimens']) {
      this.updatePrescriptionVisibility();
      this.updateReportButtonVisibility();
    }
  }

  navigateTo(url: string, params: any = {}): void {
    if( url === '/support') {
      this.eventLogger.trackEvent('help_burgermenu_clicked');
    }
    if (url === '/logout') {
      if (this.isInternalUser) return;
      this.onPopupToggle.emit(true);
      this.ui.popUpModal = { message: { type: this.appConfig.Shared.String.LOGOUT_CONFIRM },
        open: true,
        title: this.appConfig.Shared.String.LOGOUT,
        okText: this.appConfig.Shared.String.YES,
        cancelText: this.appConfig.Shared.String.NO,
        type: this.appConfig.Dialog.CONFIRMATION };
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'logout' }));
    } else this.router.navigate([url], { queryParams: params });
  }

  async getPendingResponseMap(): Promise<void> {
    const pendingResponseMap = await this.conn.findAllPendingTicketWithAwaitingResponse();
    this.isTicketAwaitingResponse = Object.values(pendingResponseMap).some(value => value === true);
  }

  /**
   * Processes regimens to determine if feedback option should be shown
   */
  processRegimens(): void {
    const activeRegimens = this.regimens?.filter((r) => r?.active === true);
    this.showFeedbackOption = this.checkOldestRegimenDeliveredAfterThreshold(activeRegimens);
  }

  updatePrescriptionVisibility(): void {
    const isDelivered = this.user?.get('orderState') === ApiClientConstant.User.OrderState.DELIVERED;
    const hasActiveRegimen = this.regimens?.some(r => r?.active);
    this.showPrescriptionButton = isDelivered && hasActiveRegimen;
  }

  updateReportButtonVisibility(): void {
    if (this.userService.isPaidUser(this.user)) return;

    const hasRegimen = (type: string): boolean => this.regimens.some(r => r?.class === type);

    this.showSkinReportButton = hasRegimen(this.appConfig.Shared.Regimen.Class.FACE);
    this.showHairReportButton = hasRegimen(this.appConfig.Shared.Regimen.Class.HAIR);
  }

  getRegimenForReportClass(regimenClass: string): any {
    return (this.regimens || []).find(
      (r: any) => r?.class === regimenClass
    ) ?? null;
  }

  navigateToSkinReport(): void {
    const regimen = this.getRegimenForReportClass(this.appConfig.Shared.Regimen.Class.FACE);
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: `burgerMenu_${regimen?.class}_report` }));
    this.router.navigate(['/report'], { queryParams: { class: 'FACE' }, state: { regimen, experiments: this.experiments } });
  }

  navigateToHairReport(): void {
    const regimen = this.getRegimenForReportClass(this.appConfig.Shared.Regimen.Class.HAIR);
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: `burgerMenu_${regimen?.class}_report` }));
    this.router.navigate(['/report'], { queryParams: { class: 'HAIR' }, state: { regimen, experiments: this.experiments } });
  }

  get isFemaleUser(): boolean {
    return this.user?.get('Gender') === ApiClientConstant.User.Gender.FEMALE;
  }

  closePopUp(event: any): any {
    this.ui.popUpModal = { open: false };
    if (event.clickOnYes) {
      setTimeout((): Promise<any> => this.router.navigate(['/logout'], { replaceUrl: true }), 500);
    }
    this.onPopupToggle.emit(false);
  }

  back(): void {
    this.showRefundPolicyOptions = false;
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  stopPropagation(event: any): void {
    event.stopPropagation();
  }

  redirectTo(url: string): void {
    this.conn.navigateToURL(url);
  }

  /**
   * Checks if the oldest delivered regimen was delivered after the configured threshold
   * @param regimens Array of regimen objects
   * @returns boolean - true if oldest delivered regimen exceeds the delivery threshold, false otherwise
   */
  checkOldestRegimenDeliveredAfterThreshold(regimens: any[]): boolean {
    if (!regimens || regimens.length === 0) {
      return false;
    }

    // Filter regimens that have a deliveredDate
    const deliveredRegimens = regimens.filter(regimen =>
      regimen.deliveredDate && typeof regimen.deliveredDate === 'number'
    );

    if (deliveredRegimens.length === 0) {
      return false;
    }

    // Find the oldest delivered regimen (smallest deliveredDate timestamp)
    const oldestDeliveredDate = Math.min(...deliveredRegimens.map(regimen => regimen.deliveredDate));

    // Calculate the difference in days between now and the oldest delivered date
    const currentTimestamp = Date.now();
    const daysDifference = (currentTimestamp - oldestDeliveredDate) / this.MILLISECONDS_PER_DAY;

    // Check if it's been at least the configured threshold days
    return daysDifference >= this.deliveryThresholdDays;
  }
}
